import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Archive,
  ArchiveRestore,
  Barcode,
  History,
  Link2,
  ListChecks,
  Loader2,
  Store,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/features/auth/auth.store';
import { env } from '@/config/env';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  livePrice,
  toBusinessItemInput,
  type AssignedStore,
  type BusinessItemData,
  type BusinessItemInput,
  type BusinessItemPrice,
  type BusinessItemTimeConstraint,
  type MasterItem,
} from './item-master.api';
import {
  useAisles,
  useArchiveBusinessItem,
  useGroups,
  useMenuSections,
  useUpdateBusinessItem,
} from './business-item.api';
import {
  BusinessItemLogsSheet,
  BusinessItemUpcsSheet,
  ItemPriceSheet,
  ModifiersSheet,
} from './business-item-drawers';
import { itemLabel } from './item-drawers';

/**
 * One item as one store sells it.
 *
 * The original was a 1,663-line drawer of stacked collapse panels; this is the
 * same fields under four tabs, because a phone cannot scroll that far usefully.
 *
 * Two shapes fight each other here and it is worth naming: the row the API
 * *answers* carries `prices` and `datas` as lists, while the model it *accepts*
 * takes a single `price` and `data`. The draft below keeps the answer's shape
 * and toBusinessItemInput() does the narrowing at the last moment.
 */

const WEIGHT_TYPES = [
  { value: 0, label: 'LB' },
  { value: 4, label: 'Unit/LB' },
];

const PRICE_METHODS = [
  { value: 0, labelKey: 'businessItem.unit' },
  { value: 1, labelKey: 'businessItem.split' },
];

const TAX_RULES = [
  { value: 0, labelKey: 'businessItem.taxNone' },
  { value: 1, labelKey: 'businessItem.taxMunicipal' },
  { value: 2, labelKey: 'businessItem.taxState' },
  { value: 3, labelKey: 'businessItem.taxBoth' },
];

const ITEM_TYPES = [
  { value: 0, labelKey: 'businessItem.inStore' },
  { value: 1, labelKey: 'businessItem.externalSupplier' },
];

/** Falls back to a blank row so a store with no price still edits cleanly. */
const BLANK_PRICE: BusinessItemPrice = {
  id: '',
  taxRule: 0,
  ordering: 99,
  basePriceMethod: 0,
  basePrice: 0,
  baseMultiple: 1,
  promoPrice: 0,
  promoMultiple: 1,
  promoPriceMethod: 0,
  promoStartDate: null,
  promoEndDate: null,
  cost: 0,
  costOfBox: 0,
  pack: 1,
  name: null,
  description: null,
  activePrice: null,
  regularPrice: null,
  promotionalPrice: null,
  hasActivePromotion: false,
  isDeleted: false,
  isActive: true,
};

const BLANK_DATA: BusinessItemData = {
  isAllNatural: false,
  isLactoseFree: false,
  isSugarFree: false,
  isKeto: false,
  isOrganic: false,
  isVegan: false,
  isVegetarian: false,
  isGlutenFree: false,
  isGrassFed: false,
  isAntioxidant: false,
  isNutFree: false,
  isNonGmo: false,
  isDeleted: false,
  hasRandomWeight: false,
  madeLocally: false,
  weightType: 0,
  minQuantity: 1,
  maxQuantity: 99,
  stepAmount: 1,
  unitPriceFactor: 1,
};

const BLANK_HOURS: BusinessItemTimeConstraint = {
  availableOnMondays: true,
  availableOnTuesdays: true,
  availableOnWednesdays: true,
  availableOnThursdays: true,
  availableOnFridays: true,
  availableOnSaturdays: true,
  availableOnSundays: true,
  hourDelay: 0,
  minuteDelay: 0,
  limitDate: null,
};

const DAYS = [
  'availableOnMondays',
  'availableOnTuesdays',
  'availableOnWednesdays',
  'availableOnThursdays',
  'availableOnFridays',
  'availableOnSaturdays',
  'availableOnSundays',
] as const;

function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex items-center justify-between gap-3 rounded-lg border px-3 py-2',
        disabled && 'opacity-50'
      )}
    >
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
  step,
  min,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (value: number) => void;
  step?: string;
  min?: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type="number"
        inputMode="decimal"
        step={step}
        min={min}
        value={value ?? 0}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  );
}

/** `datetime-local` gives `YYYY-MM-DDTHH:mm`; the API wants seconds on it. */
function toApiDate(value: string) {
  if (!value) return null;
  return value.length < 17 ? `${value}:00.000` : value;
}

function toInputDate(value: string | null) {
  if (!value) return '';
  return value.slice(0, 16).replace(' ', 'T');
}

export function BusinessItemEditor({
  store,
  item,
  otherStores,
  open,
  onOpenChange,
}: {
  store: AssignedStore | null;
  /** The catalogue item, for the header. */
  item: MasterItem | null;
  /** Every other store stocking the item, for "apply to multiple". */
  otherStores: AssignedStore[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();

  const update = useUpdateBusinessItem();
  const archive = useArchiveBusinessItem();
  const sections = useMenuSections(open ? store?.businessId ?? null : null);
  const aisles = useAisles(open ? store?.businessId ?? null : null);
  const groups = useGroups(open ? store?.businessId ?? null : null);

  const isSuperAdmin = useAuthStore((state) =>
    !!state.profile?.roles?.includes('SuperAdmin')
  );
  const managesMultiplePrices = useAuthStore(
    (state) => !!state.state?.managesMultiplePrices
  );

  const [draft, setDraft] = useState<AssignedStore | null>(null);
  const [hydrated, setHydrated] = useState<string | null>(null);
  const [applyTo, setApplyTo] = useState<string[]>([]);
  const [freeAisle, setFreeAisle] = useState(false);
  const [freeGroup, setFreeGroup] = useState(false);
  const [showUpcs, setShowUpcs] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showModifiers, setShowModifiers] = useState(false);
  const [editingPrice, setEditingPrice] = useState<BusinessItemPrice | null>(
    null
  );

  if (store && hydrated !== store.id) {
    setHydrated(store.id);
    setDraft(store);
    setApplyTo([]);
    setFreeAisle(false);
    setFreeGroup(false);
  }

  if (!draft || !store) return null;

  const set = (patch: Partial<AssignedStore>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const price = livePrice(draft) ?? BLANK_PRICE;
  const multiPrice = managesMultiplePrices || (draft.prices ?? []).length > 1;
  const data = (draft.datas ?? [])[0] ?? BLANK_DATA;
  const hours = draft.timeConstraint ?? BLANK_HOURS;

  const setPrice = (patch: Partial<BusinessItemPrice>) => {
    const next = { ...price, ...patch };
    const rest = (draft.prices ?? []).filter((row) => row.id !== price.id);
    set({ prices: [next, ...rest] });
  };
  const setData = (patch: Partial<BusinessItemData>) =>
    set({ datas: [{ ...data, ...patch }] });
  const setHours = (patch: Partial<BusinessItemTimeConstraint>) =>
    set({ timeConstraint: { ...hours, ...patch } });

  const failed = () => toast.error(t('itemMaster.actionFailed'));

  function save() {
    const body: BusinessItemInput = {
      ...toBusinessItemInput(draft!),
      ...(applyTo.length > 0 && { otherBusinessItemIds: applyTo }),
    };

    update.mutate(
      { businessItemId: draft!.id, body },
      {
        onSuccess: () => {
          toast.success(t('itemMaster.saved'));
          onOpenChange(false);
        },
        onError: failed,
      }
    );
  }

  const productUrl = env.specificProductUrl
    ? `${env.specificProductUrl}/${store.businessId}/${store.id}`
    : null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle className="flex items-center gap-2">
              <Store className="size-4 shrink-0" />
              <span className="truncate">{store.businessName}</span>
            </SheetTitle>
            <p className="text-muted-foreground truncate text-sm">
              {item ? itemLabel(item) : store.upc}
            </p>
            {draft.isDeleted && (
              <Badge variant="secondary" className="w-fit">
                {t('itemMaster.archived')}
              </Badge>
            )}
          </SheetHeader>

          <div className="space-y-3 p-4">
            {/* The lists this store's offer owns, and the two read-only
                actions the original kept in the drawer's top corner. */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUpcs(true)}
              >
                <Barcode className="size-3.5" />
                {t('businessItem.upcs')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowModifiers(true)}
              >
                <ListChecks className="size-3.5" />
                {t('businessItem.modifiers')}
              </Button>
              {productUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(productUrl);
                    toast.success(t('businessItem.linkCopied'));
                  }}
                >
                  <Link2 className="size-3.5" />
                  {t('businessItem.link')}
                </Button>
              )}
              {isSuperAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLogs(true)}
                >
                  <History className="size-3.5" />
                  {t('itemMaster.logs')}
                </Button>
              )}
            </div>

            <Tabs defaultValue="general">
              <TabsList className="w-full">
                <TabsTrigger value="general" className="flex-1">
                  {t('businessItem.general')}
                </TabsTrigger>
                <TabsTrigger value="pricing" className="flex-1">
                  {t('businessItem.pricing')}
                </TabsTrigger>
                <TabsTrigger value="hours" className="flex-1">
                  {t('businessItem.hours')}
                </TabsTrigger>
                <TabsTrigger value="flags" className="flex-1">
                  {t('businessItem.flags')}
                </TabsTrigger>
              </TabsList>

              {/* ------------------------------------------- general */}
              <TabsContent value="general" className="space-y-3">
                <div className="bg-muted/40 rounded-lg border p-3">
                  <p className="text-muted-foreground text-xs">
                    {t('businessItem.primaryUpc')}
                  </p>
                  <p className="font-mono text-sm">{draft.upc || '—'}</p>
                </div>

                {/* Group and aisle are free text backed by what the store
                    already uses, so a new one can be coined without leaving
                    the list behind. */}
                <PickOrType
                  label={t('businessItem.group')}
                  hint={t('businessItem.groupHint')}
                  options={groups.data ?? []}
                  loading={groups.isLoading}
                  value={draft.department ?? ''}
                  free={freeGroup}
                  onFree={setFreeGroup}
                  onChange={(department) => set({ department })}
                />

                <PickOrType
                  label={t('businessItem.aisle')}
                  options={aisles.data ?? []}
                  loading={aisles.isLoading}
                  value={draft.aisle ?? ''}
                  free={freeAisle}
                  onFree={setFreeAisle}
                  onChange={(aisle) => set({ aisle })}
                />

                <div className="space-y-1.5">
                  <Label>{t('businessItem.section')}</Label>
                  {sections.isLoading ? (
                    <Skeleton className="h-9 w-full" />
                  ) : (
                    <Select
                      value={draft.sectionId ?? ''}
                      onValueChange={(sectionId) => set({ sectionId })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('businessItem.pickSection')} />
                      </SelectTrigger>
                      <SelectContent>
                        {(sections.data ?? []).map((section) => {
                          const name =
                            i18n.language === 'en'
                              ? section.nameEng || section.name
                              : section.name || section.nameEng;
                          const parent =
                            i18n.language === 'en'
                              ? section.superSectionNameEng
                                || section.superSectionName
                              : section.superSectionName
                                || section.superSectionNameEng;
                          return (
                            <SelectItem key={section.id} value={section.id}>
                              {parent ? `${name} @ ${parent}` : name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <NumberField
                    label={t('businessItem.minimum')}
                    value={data.minQuantity}
                    min={0.1}
                    step="0.1"
                    onChange={(minQuantity) => setData({ minQuantity })}
                  />
                  <NumberField
                    label={t('businessItem.maximum')}
                    value={data.maxQuantity}
                    min={1}
                    onChange={(maxQuantity) => setData({ maxQuantity })}
                  />
                  <NumberField
                    label={t('businessItem.stepAmount')}
                    value={data.stepAmount}
                    min={0.1}
                    step="0.1"
                    onChange={(stepAmount) => setData({ stepAmount })}
                  />
                  <NumberField
                    label={t('businessItem.pack')}
                    value={price.pack}
                    min={1}
                    onChange={(pack) => setPrice({ pack })}
                  />
                </div>

                <Toggle
                  label={t('businessItem.randomWeight')}
                  checked={data.hasRandomWeight}
                  onChange={(hasRandomWeight) => setData({ hasRandomWeight })}
                />

                <div className="space-y-1.5">
                  <Label>{t('businessItem.weightType')}</Label>
                  <Select
                    value={String(data.weightType)}
                    disabled={!data.hasRandomWeight}
                    onValueChange={(value) =>
                      setData({ weightType: Number(value) })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WEIGHT_TYPES.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={String(option.value)}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <NumberField
                  label={t('businessItem.unitPerPackage')}
                  value={data.unitPriceFactor}
                  min={0.1}
                  step="0.1"
                  onChange={(unitPriceFactor) => setData({ unitPriceFactor })}
                />

                <div className="space-y-1.5">
                  <Label>{t('businessItem.customSearchTags')}</Label>
                  <Input
                    value={draft.customSearchTags ?? ''}
                    onChange={(event) =>
                      set({ customSearchTags: event.target.value })
                    }
                  />
                  <p className="text-muted-foreground text-xs">
                    {t('businessItem.customSearchTagsHint')}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label>{t('businessItem.itemType')}</Label>
                  {/* The row only carries a type when the store sets one;
                      the original disabled the field otherwise, and so does
                      this rather than inventing a default. */}
                  <Select
                    value={String(draft.type ?? 0)}
                    disabled={!draft.type}
                    onValueChange={(value) => set({ type: Number(value) })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ITEM_TYPES.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={String(option.value)}
                        >
                          {t(option.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              {/* ------------------------------------------- pricing */}
              <TabsContent value="pricing" className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <NumberField
                    label={t('businessItem.munTax')}
                    value={draft.munTaxPct}
                    min={0}
                    step="0.01"
                    onChange={(munTaxPct) => set({ munTaxPct })}
                  />
                  <NumberField
                    label={t('businessItem.stateTax')}
                    value={draft.stateTaxPct}
                    min={0}
                    step="0.01"
                    onChange={(stateTaxPct) => set({ stateTaxPct })}
                  />
                </div>

                {/* Several prices are edited one at a time in their own
                    drawer; a single price is edited inline. An item that
                    already carries more than one gets the list either way —
                    the inline form would collapse the rest into one. */}
                {multiPrice ? (
                  <>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setEditingPrice(BLANK_PRICE)}
                    >
                      {t('businessItem.addPrice')}
                    </Button>
                    {(draft.prices ?? []).length === 0 ? (
                      <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
                        {t('businessItem.noPrices')}
                      </p>
                    ) : (
                      <div className="divide-y rounded-xl border">
                        {(draft.prices ?? []).map((row) => (
                          <button
                            key={row.id}
                            type="button"
                            onClick={() => setEditingPrice(row)}
                            className="hover:bg-accent flex w-full items-center justify-between gap-3 p-3 text-left transition-colors"
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium">
                                {row.name || t('businessItem.unnamedPrice')}
                              </span>
                              {!row.isActive && (
                                <Badge variant="secondary">
                                  {t('businessItem.inactive')}
                                </Badge>
                              )}
                            </span>
                            <span className="shrink-0 font-semibold tabular-nums">
                              {formatCurrency(row.activePrice)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium">
                      {t('businessItem.basePrice')}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <NumberField
                        label={t('businessItem.price')}
                        value={price.basePrice}
                        min={0}
                        step="0.01"
                        onChange={(basePrice) => setPrice({ basePrice })}
                      />
                      <NumberField
                        label={t('businessItem.multiple')}
                        value={price.baseMultiple}
                        min={1}
                        onChange={(baseMultiple) => setPrice({ baseMultiple })}
                      />
                      <NumberField
                        label={t('businessItem.cost')}
                        value={price.cost}
                        min={0}
                        step="0.01"
                        onChange={(cost) => setPrice({ cost })}
                      />
                      <NumberField
                        label={t('businessItem.costOfBox')}
                        value={price.costOfBox}
                        min={0}
                        step="0.01"
                        onChange={(costOfBox) => setPrice({ costOfBox })}
                      />
                    </div>

                    <EnumField
                      label={t('businessItem.priceMethod')}
                      value={price.basePriceMethod}
                      options={PRICE_METHODS}
                      onChange={(basePriceMethod) =>
                        setPrice({ basePriceMethod })
                      }
                    />
                    <EnumField
                      label={t('businessItem.taxRule')}
                      value={price.taxRule}
                      options={TAX_RULES}
                      onChange={(taxRule) => setPrice({ taxRule })}
                    />

                    <p className="pt-2 text-sm font-medium">
                      {t('businessItem.promoPrice')}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <NumberField
                        label={t('businessItem.price')}
                        value={price.promoPrice}
                        min={0}
                        step="0.01"
                        onChange={(promoPrice) => setPrice({ promoPrice })}
                      />
                      <NumberField
                        label={t('businessItem.multiple')}
                        value={price.promoMultiple}
                        min={1}
                        onChange={(promoMultiple) => setPrice({ promoMultiple })}
                      />
                    </div>
                    <EnumField
                      label={t('businessItem.priceMethod')}
                      value={price.promoPriceMethod}
                      options={PRICE_METHODS}
                      onChange={(promoPriceMethod) =>
                        setPrice({ promoPriceMethod })
                      }
                    />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label>{t('businessItem.startDate')}</Label>
                        <Input
                          type="datetime-local"
                          value={toInputDate(price.promoStartDate)}
                          onChange={(event) =>
                            setPrice({
                              promoStartDate: toApiDate(event.target.value),
                            })
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>{t('businessItem.endDate')}</Label>
                        <Input
                          type="datetime-local"
                          value={toInputDate(price.promoEndDate)}
                          onChange={(event) =>
                            setPrice({
                              promoEndDate: toApiDate(event.target.value),
                            })
                          }
                        />
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* --------------------------------------------- hours */}
              <TabsContent value="hours" className="space-y-3">
                <div className="space-y-1.5">
                  <Label>{t('businessItem.limitDate')}</Label>
                  <Input
                    type="date"
                    value={(hours.limitDate ?? '').slice(0, 10)}
                    onChange={(event) =>
                      setHours({ limitDate: event.target.value || null })
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <NumberField
                    label={t('businessItem.hourDelay')}
                    value={hours.hourDelay}
                    min={0}
                    onChange={(hourDelay) => setHours({ hourDelay })}
                  />
                  <NumberField
                    label={t('businessItem.minuteDelay')}
                    value={hours.minuteDelay}
                    min={0}
                    onChange={(minuteDelay) => setHours({ minuteDelay })}
                  />
                </div>

                <div className="space-y-2">
                  {DAYS.map((day) => (
                    <Toggle
                      key={day}
                      label={t(`businessItem.${day}`)}
                      checked={hours[day]}
                      onChange={(value) => setHours({ [day]: value })}
                    />
                  ))}
                </div>
              </TabsContent>

              {/* --------------------------------------------- flags */}
              <TabsContent value="flags" className="space-y-2">
                <Toggle
                  label={t('itemMaster.visible')}
                  checked={draft.isVisible}
                  onChange={(isVisible) => set({ isVisible })}
                />
                <Toggle
                  label={t('itemMaster.available')}
                  checked={draft.isAvailable}
                  onChange={(isAvailable) => set({ isAvailable })}
                />
                <Toggle
                  label={t('itemMaster.affectsInventory')}
                  checked={draft.affectsInventory}
                  onChange={(affectsInventory) => set({ affectsInventory })}
                />
                <Toggle
                  label={t('businessItem.allowedInLayaway')}
                  checked={draft.allowedInLayaway}
                  onChange={(allowedInLayaway) => set({ allowedInLayaway })}
                />
                <Toggle
                  label={t('businessItem.hideInKiosk')}
                  checked={draft.hideInKiosk}
                  onChange={(hideInKiosk) => set({ hideInKiosk })}
                />
                <Toggle
                  label={t('businessItem.hideInSelfCheckout')}
                  checked={draft.hideInSelfCheckout}
                  onChange={(hideInSelfCheckout) => set({ hideInSelfCheckout })}
                />
                <Toggle
                  label={t('businessItem.promoted')}
                  checked={draft.isPromoted}
                  onChange={(isPromoted) => set({ isPromoted })}
                />
                <Toggle
                  label={t('businessItem.madeLocally')}
                  checked={data.madeLocally}
                  onChange={(madeLocally) => setData({ madeLocally })}
                />

                {[1, 2, 3, 4, 5, 6].map((index) => {
                  const key =
                    `customBooleanProperty0${index}` as keyof AssignedStore;
                  return (
                    <Toggle
                      key={key}
                      label={`${t('businessItem.customDepartment')} ${index}`}
                      checked={!!draft[key]}
                      onChange={(value) => set({ [key]: value })}
                    />
                  );
                })}

                <p className="pt-2 text-sm font-medium">
                  {t('businessItem.dietary')}
                </p>
                {(
                  [
                    ['isAllNatural', 'natural'],
                    ['isAntioxidant', 'antioxidant'],
                    ['isGlutenFree', 'glutenFree'],
                    ['isGrassFed', 'grassFed'],
                    ['isKeto', 'keto'],
                    ['isLactoseFree', 'lactoseFree'],
                    ['isNonGmo', 'nonGmo'],
                    ['isNutFree', 'nutFree'],
                    ['isOrganic', 'organic'],
                    ['isSugarFree', 'sugarFree'],
                    ['isVegan', 'vegan'],
                    ['isVegetarian', 'vegetarian'],
                  ] as const
                ).map(([field, labelKey]) => (
                  <Toggle
                    key={field}
                    label={t(`businessItem.${labelKey}`)}
                    checked={data[field]}
                    onChange={(value) => setData({ [field]: value })}
                  />
                ))}
              </TabsContent>
            </Tabs>

            {/* Applying the same edit to other stores is one request: the
                server takes the target ids alongside the body. */}
            {/* Any number of prices can travel now: the update takes a
                `prices` array, so this is no longer single-price only. */}
            {otherStores.length > 0 && (
              <div className="bg-muted/40 space-y-2 rounded-xl border p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">
                    {t('businessItem.applyToMultiple')}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7"
                    onClick={() =>
                      setApplyTo(
                        applyTo.length === otherStores.length
                          ? []
                          : otherStores.map((row) => row.id)
                      )
                    }
                  >
                    {applyTo.length === otherStores.length
                      ? t('itemMaster.clearAll')
                      : t('itemMaster.selectAll')}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {otherStores.map((row) => {
                    const picked = applyTo.includes(row.id);
                    return (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() =>
                          setApplyTo((current) =>
                            picked
                              ? current.filter((id) => id !== row.id)
                              : [...current, row.id]
                          )
                        }
                        className={cn(
                          'rounded-full border px-2.5 py-1 text-xs transition-colors',
                          picked
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'bg-background hover:bg-accent'
                        )}
                      >
                        {row.businessName}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              {/* Archiving takes the item off this store's shelves and leaves
                  every other store alone, so it says which store it means. */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant={draft.isDeleted ? 'outline' : 'destructive'}
                    disabled={update.isPending || archive.isPending}
                  >
                    {archive.isPending && <Loader2 className="animate-spin" />}
                    {draft.isDeleted ? (
                      <ArchiveRestore className="size-4" />
                    ) : (
                      <Archive className="size-4" />
                    )}
                    {draft.isDeleted
                      ? t('itemMaster.restore')
                      : t('itemMaster.archive')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {draft.isDeleted
                        ? t('itemMaster.restore')
                        : t('itemMaster.archive')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t(
                        draft.isDeleted
                          ? 'businessItem.restorePrompt'
                          : 'businessItem.archivePrompt',
                        { store: store.businessName ?? '' }
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className={
                        draft.isDeleted
                          ? undefined
                          : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                      }
                      onClick={() =>
                        archive.mutate(
                          {
                            businessItemId: draft.id,
                            archived: !draft.isDeleted,
                          },
                          {
                            onSuccess: () => {
                              toast.success(
                                draft.isDeleted
                                  ? t('itemMaster.restored')
                                  : t('businessItem.archived')
                              );
                              onOpenChange(false);
                            },
                            onError: failed,
                          }
                        )
                      }
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={update.isPending}
                  onClick={() => onOpenChange(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button disabled={update.isPending} onClick={save}>
                  {update.isPending && <Loader2 className="animate-spin" />}
                  {t('common.save')}
                </Button>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <BusinessItemUpcsSheet
        businessItemId={store.id}
        open={showUpcs}
        onOpenChange={setShowUpcs}
      />
      <BusinessItemLogsSheet
        businessItemId={store.id}
        open={showLogs}
        onOpenChange={setShowLogs}
      />
      <ModifiersSheet
        businessItemId={store.id}
        businessId={store.businessId}
        itemId={store.itemId}
        open={showModifiers}
        onOpenChange={setShowModifiers}
      />
      <ItemPriceSheet
        businessItemId={store.id}
        siblings={draft.prices ?? []}
        price={editingPrice}
        open={!!editingPrice}
        onOpenChange={(next) => !next && setEditingPrice(null)}
      />
    </>
  );
}

/** A list to pick from, with an escape hatch into free text. */
function PickOrType({
  label,
  hint,
  options,
  value,
  free,
  onFree,
  onChange,
  loading,
}: {
  label: string;
  hint?: string;
  options: string[];
  value: string;
  free: boolean;
  onFree: (free: boolean) => void;
  onChange: (value: string) => void;
  /** The options are still arriving; the saved value would read as unset. */
  loading?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        <Button
          variant="ghost"
          size="sm"
          className="h-7"
          onClick={() => onFree(!free)}
        >
          {free ? t('businessItem.pickFromList') : t('businessItem.typeNew')}
        </Button>
      </div>
      {free ? (
        <Input value={value} onChange={(e) => onChange(e.target.value)} />
      ) : loading ? (
        <Skeleton className="h-9 w-full" />
      ) : (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={label} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

function EnumField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: number;
  options: { value: number; labelKey: string }[];
  onChange: (value: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={String(option.value)}>
              {t(option.labelKey)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
